import { Flag, Info, Palmtree } from 'lucide-react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface HeatmapInfoTooltipStrings {
  /** Card heading + icon accessible label, e.g. "How this heatmap works". */
  infoTitle?: string
  /** Term for the allocation metric, e.g. "Allocation". */
  allocationLabel?: string
  /** Term for the availability metric, e.g. "Availability". */
  availabilityLabel?: string
  /** Allocation formula line. */
  infoAllocated?: string
  /** Availability formula line. */
  infoAvailable?: string
  /** Note that allocation is PTO/stat-blind (won't sum to 100%). */
  infoPtoBlind?: string
  /** Capacity = weekdays only. */
  infoCapacity?: string
  /** Weekly-column bucketing rule (the partial-week / weekend-spill nuance). */
  infoWeekly?: string
  /** Palm-tree marker explainer. */
  infoPalm?: string
  /** End-of-work flag marker + grey-out explainer. */
  infoEndDate?: string
}

interface HeatmapInfoTooltipProps {
  strings?: HeatmapInfoTooltipStrings
}

/**
 * Small "i" affordance next to the heatmap title. Hovering (or focusing) it opens a card
 * explaining how the percentages are computed and — the part that trips people up — how
 * weekly vs monthly columns bucket days at month/engagement edges. Purely informational.
 */
export function HeatmapInfoTooltip({ strings }: HeatmapInfoTooltipProps) {
  const title = strings?.infoTitle || 'How this heatmap works'
  const rows: Array<{ term: string; detail?: string }> = [
    { term: strings?.allocationLabel || 'Allocation', detail: strings?.infoAllocated || 'Planned engagement hours ÷ capacity.' },
    { term: strings?.availabilityLabel || 'Availability', detail: strings?.infoAvailable || '(Capacity − planned hours − PTO − stat holidays) ÷ capacity.' },
  ]

  const notes: Array<string | undefined> = [
    strings?.infoPtoBlind || 'Allocation ignores PTO & stat holidays, so allocation and availability won’t add up to 100%.',
    strings?.infoCapacity || 'Capacity counts weekdays only (Mon–Fri); weekends never count.',
    strings?.infoWeekly || 'A week’s allocation and availability are computed across the whole week. So an engagement at 8 hours per day may read 100% allocated for a week it fully covers, but less than 100% for its first week if it started mid-week.',
  ]

  const palmNote = strings?.infoPalm || 'This palm-tree marker flags PTO or stat holidays in a cell. Hover over it for the exact dates.'
  const endDateNote = strings?.infoEndDate || 'This flag marks the worker’s end date: their termination date if a termination is pending, otherwise their profile end date. Cells after it are greyed out. A red percentage on a greyed cell means work is already assigned past that date. Salaried workers have no profile end date, so their rows carry no flag unless a termination is pending.'

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={title}
            // Keep the card up on click and don't steal a mouse-focus ring — Radix closes the
            // tooltip from its own pointer-down/click handlers, but composeEventHandlers skips
            // those when ours calls preventDefault. preventDefault on pointer-down also
            // suppresses the mouse focus ring (keyboard Tab still focuses → focus-visible below).
            onPointerDown={(e) => e.preventDefault()}
            onClick={(e) => e.preventDefault()}
            className="inline-flex items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-current"
          >
            <Info className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        {/* Portal keeps the card above the sticky table header / scroll container. */}
        <TooltipPrimitive.Portal>
          <TooltipContent
            side="bottom"
            align="start"
            className="w-[460px] max-w-[calc(100vw-2rem)] p-3 text-xs normal-case tracking-normal font-normal"
            // A pointer-down on the trigger counts as "outside" this DismissableLayer and would
            // dismiss it; preventDefault keeps the card up on click (hover-out still closes it).
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <div className="font-semibold mb-1.5">{title}</div>
            <dl className="space-y-1">
              {rows.map(r => (
                <div key={r.term} className="flex gap-1.5">
                  <dt className="font-medium shrink-0">{r.term}</dt>
                  <dd className="text-muted-foreground">{r.detail}</dd>
                </div>
              ))}
            </dl>
            <ul className="mt-2 space-y-1 list-disc pl-4 text-muted-foreground">
              {notes.filter(Boolean).map((n, i) => (
                <li key={i}>{n}</li>
              ))}
              {/* Final bullet shows the real palm-tree icon in place of the disc so the
                  reader can match it to what they'll see in a cell. */}
              <li className="list-none -ml-4 flex items-start gap-1.5">
                <Palmtree className="w-3.5 h-3.5 shrink-0 mt-[1px]" aria-hidden="true" />
                <span>{palmNote}</span>
              </li>
              <li className="list-none -ml-4 flex items-start gap-1.5">
                <Flag className="w-3.5 h-3.5 shrink-0 mt-[1px]" aria-hidden="true" />
                <span>{endDateNote}</span>
              </li>
            </ul>
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  )
}
